import notificationService from "../../../apis/services/admin/notificationService";

const settleSequentially = async (items, execute) => {
  const results = [];
  for (const item of items) {
    try {
      results.push({ status: "fulfilled", value: await execute(item) });
    } catch (reason) {
      results.push({ status: "rejected", reason });
    }
  }
  return results;
};

// Action Types - defined locally
const FETCH_NOTIFICATION_RECIPIENTS_ATTEMPT = "FETCH_NOTIFICATION_RECIPIENTS_ATTEMPT";
const FETCH_NOTIFICATION_RECIPIENTS_SUCCESS = "FETCH_NOTIFICATION_RECIPIENTS_SUCCESS";
const FETCH_NOTIFICATION_RECIPIENTS_ERROR = "FETCH_NOTIFICATION_RECIPIENTS_ERROR";

const CREATE_NOTIFICATION_RECIPIENT_ATTEMPT = "CREATE_NOTIFICATION_RECIPIENT_ATTEMPT";
const CREATE_NOTIFICATION_RECIPIENT_SUCCESS = "CREATE_NOTIFICATION_RECIPIENT_SUCCESS";
const CREATE_NOTIFICATION_RECIPIENT_ERROR = "CREATE_NOTIFICATION_RECIPIENT_ERROR";
const CREATE_NOTIFICATION_RECIPIENT_RESET_STATE = "CREATE_NOTIFICATION_RECIPIENT_RESET_STATE";

const DELETE_NOTIFICATION_RECIPIENT_ATTEMPT = "DELETE_NOTIFICATION_RECIPIENT_ATTEMPT";
const DELETE_NOTIFICATION_RECIPIENT_SUCCESS = "DELETE_NOTIFICATION_RECIPIENT_SUCCESS";
const DELETE_NOTIFICATION_RECIPIENT_ERROR = "DELETE_NOTIFICATION_RECIPIENT_ERROR";
const DELETE_NOTIFICATION_RECIPIENT_RESET_STATE = "DELETE_NOTIFICATION_RECIPIENT_RESET_STATE";

const TOGGLE_NOTIFICATION_RECIPIENT_ATTEMPT = "TOGGLE_NOTIFICATION_RECIPIENT_ATTEMPT";
const TOGGLE_NOTIFICATION_RECIPIENT_SUCCESS = "TOGGLE_NOTIFICATION_RECIPIENT_SUCCESS";
const TOGGLE_NOTIFICATION_RECIPIENT_ERROR = "TOGGLE_NOTIFICATION_RECIPIENT_ERROR";

const UPDATE_NOTIFICATION_RECIPIENT_ATTEMPT = "UPDATE_NOTIFICATION_RECIPIENT_ATTEMPT";
const UPDATE_NOTIFICATION_RECIPIENT_SUCCESS = "UPDATE_NOTIFICATION_RECIPIENT_SUCCESS";
const UPDATE_NOTIFICATION_RECIPIENT_ERROR = "UPDATE_NOTIFICATION_RECIPIENT_ERROR";
const UPDATE_NOTIFICATION_RECIPIENT_RESET_STATE = "UPDATE_NOTIFICATION_RECIPIENT_RESET_STATE";

// Export action types for reducer
export {
  FETCH_NOTIFICATION_RECIPIENTS_ATTEMPT,
  FETCH_NOTIFICATION_RECIPIENTS_SUCCESS,
  FETCH_NOTIFICATION_RECIPIENTS_ERROR,
  CREATE_NOTIFICATION_RECIPIENT_ATTEMPT,
  CREATE_NOTIFICATION_RECIPIENT_SUCCESS,
  CREATE_NOTIFICATION_RECIPIENT_ERROR,
  CREATE_NOTIFICATION_RECIPIENT_RESET_STATE,
  DELETE_NOTIFICATION_RECIPIENT_ATTEMPT,
  DELETE_NOTIFICATION_RECIPIENT_SUCCESS,
  DELETE_NOTIFICATION_RECIPIENT_ERROR,
  DELETE_NOTIFICATION_RECIPIENT_RESET_STATE,
  TOGGLE_NOTIFICATION_RECIPIENT_ATTEMPT,
  TOGGLE_NOTIFICATION_RECIPIENT_SUCCESS,
  TOGGLE_NOTIFICATION_RECIPIENT_ERROR,
  UPDATE_NOTIFICATION_RECIPIENT_ATTEMPT,
  UPDATE_NOTIFICATION_RECIPIENT_SUCCESS,
  UPDATE_NOTIFICATION_RECIPIENT_ERROR,
  UPDATE_NOTIFICATION_RECIPIENT_RESET_STATE,
};

// Notification Recipients (global)
export const fetchNotificationRecipients = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: FETCH_NOTIFICATION_RECIPIENTS_ATTEMPT });
      const { data } = await notificationService.fetchRecipients();
      dispatch({ type: FETCH_NOTIFICATION_RECIPIENTS_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: FETCH_NOTIFICATION_RECIPIENTS_ERROR, payload: err });
    }
  };
};

export const createNotificationRecipient = (recipientData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_ATTEMPT });
      const data = await notificationService.createRecipient(recipientData);
      dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_ERROR, payload: err });
    }
  };
};

export const createNotificationRecipientsBatch = (recipientsData) => {
  return async (dispatch) => {
    dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_ATTEMPT });
    const results = await settleSequentially(recipientsData, data => notificationService.createRecipient(data));
    const failures = results.filter(result => result.status === "rejected");
    if (failures.length) {
      const error = new Error(`${failures.length} of ${results.length} recipient operations failed`);
      error.failures = failures.map(result => result.reason);
      dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_ERROR, payload: error });
      return { success: false, results };
    }
    dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_SUCCESS, payload: { data: results.map(result => result.value) } });
    return { success: true, results };
  };
};

export const createNotificationRecipientResetState = () => {
  return (dispatch) => {
    dispatch({ type: CREATE_NOTIFICATION_RECIPIENT_RESET_STATE });
  };
};

export const deleteNotificationRecipient = (id) => {
  return async (dispatch) => {
    try {
      dispatch({ type: DELETE_NOTIFICATION_RECIPIENT_ATTEMPT });
      await notificationService.deleteRecipient(id);
      dispatch({ type: DELETE_NOTIFICATION_RECIPIENT_SUCCESS, payload: { id } });
    } catch (err) {
      dispatch({ type: DELETE_NOTIFICATION_RECIPIENT_ERROR, payload: err });
    }
  };
};

export const deleteNotificationRecipientResetState = () => {
  return (dispatch) => {
    dispatch({ type: DELETE_NOTIFICATION_RECIPIENT_RESET_STATE });
  };
};

export const toggleNotificationRecipient = (id) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TOGGLE_NOTIFICATION_RECIPIENT_ATTEMPT });
      const data = await notificationService.toggleRecipient(id);
      dispatch({ type: TOGGLE_NOTIFICATION_RECIPIENT_SUCCESS, payload: { data, id } });
    } catch (err) {
      dispatch({ type: TOGGLE_NOTIFICATION_RECIPIENT_ERROR, payload: err });
    }
  };
};

export const updateNotificationRecipient = (id, recipientData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_NOTIFICATION_RECIPIENT_ATTEMPT });
      const data = await notificationService.updateRecipient(id, recipientData);
      dispatch({ type: UPDATE_NOTIFICATION_RECIPIENT_SUCCESS, payload: { data, id } });
    } catch (err) {
      dispatch({ type: UPDATE_NOTIFICATION_RECIPIENT_ERROR, payload: err });
    }
  };
};

export const updateNotificationRecipientResetState = () => {
  return (dispatch) => {
    dispatch({ type: UPDATE_NOTIFICATION_RECIPIENT_RESET_STATE });
  };
};

// Subscription Action Types
const FETCH_NOTIFICATION_SUBSCRIPTIONS_ATTEMPT = "FETCH_NOTIFICATION_SUBSCRIPTIONS_ATTEMPT";
const FETCH_NOTIFICATION_SUBSCRIPTIONS_SUCCESS = "FETCH_NOTIFICATION_SUBSCRIPTIONS_SUCCESS";
const FETCH_NOTIFICATION_SUBSCRIPTIONS_ERROR = "FETCH_NOTIFICATION_SUBSCRIPTIONS_ERROR";

const CREATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT = "CREATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT";
const CREATE_NOTIFICATION_SUBSCRIPTION_SUCCESS = "CREATE_NOTIFICATION_SUBSCRIPTION_SUCCESS";
const CREATE_NOTIFICATION_SUBSCRIPTION_ERROR = "CREATE_NOTIFICATION_SUBSCRIPTION_ERROR";
const CREATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE = "CREATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE";

const DELETE_NOTIFICATION_SUBSCRIPTION_ATTEMPT = "DELETE_NOTIFICATION_SUBSCRIPTION_ATTEMPT";
const DELETE_NOTIFICATION_SUBSCRIPTION_SUCCESS = "DELETE_NOTIFICATION_SUBSCRIPTION_SUCCESS";
const DELETE_NOTIFICATION_SUBSCRIPTION_ERROR = "DELETE_NOTIFICATION_SUBSCRIPTION_ERROR";
const DELETE_NOTIFICATION_SUBSCRIPTION_RESET_STATE = "DELETE_NOTIFICATION_SUBSCRIPTION_RESET_STATE";

const TOGGLE_NOTIFICATION_SUBSCRIPTION_ATTEMPT = "TOGGLE_NOTIFICATION_SUBSCRIPTION_ATTEMPT";
const TOGGLE_NOTIFICATION_SUBSCRIPTION_SUCCESS = "TOGGLE_NOTIFICATION_SUBSCRIPTION_SUCCESS";
const TOGGLE_NOTIFICATION_SUBSCRIPTION_ERROR = "TOGGLE_NOTIFICATION_SUBSCRIPTION_ERROR";

const UPDATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT = "UPDATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT";
const UPDATE_NOTIFICATION_SUBSCRIPTION_SUCCESS = "UPDATE_NOTIFICATION_SUBSCRIPTION_SUCCESS";
const UPDATE_NOTIFICATION_SUBSCRIPTION_ERROR = "UPDATE_NOTIFICATION_SUBSCRIPTION_ERROR";
const UPDATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE = "UPDATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE";

const FETCH_BUSINESS_UNITS_ATTEMPT = "FETCH_BUSINESS_UNITS_ATTEMPT";
const FETCH_BUSINESS_UNITS_SUCCESS = "FETCH_BUSINESS_UNITS_SUCCESS";
const FETCH_BUSINESS_UNITS_ERROR = "FETCH_BUSINESS_UNITS_ERROR";

export {
  FETCH_NOTIFICATION_SUBSCRIPTIONS_ATTEMPT,
  FETCH_NOTIFICATION_SUBSCRIPTIONS_SUCCESS,
  FETCH_NOTIFICATION_SUBSCRIPTIONS_ERROR,
  CREATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT,
  CREATE_NOTIFICATION_SUBSCRIPTION_SUCCESS,
  CREATE_NOTIFICATION_SUBSCRIPTION_ERROR,
  CREATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE,
  DELETE_NOTIFICATION_SUBSCRIPTION_ATTEMPT,
  DELETE_NOTIFICATION_SUBSCRIPTION_SUCCESS,
  DELETE_NOTIFICATION_SUBSCRIPTION_ERROR,
  DELETE_NOTIFICATION_SUBSCRIPTION_RESET_STATE,
  TOGGLE_NOTIFICATION_SUBSCRIPTION_ATTEMPT,
  TOGGLE_NOTIFICATION_SUBSCRIPTION_SUCCESS,
  TOGGLE_NOTIFICATION_SUBSCRIPTION_ERROR,
  UPDATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT,
  UPDATE_NOTIFICATION_SUBSCRIPTION_SUCCESS,
  UPDATE_NOTIFICATION_SUBSCRIPTION_ERROR,
  UPDATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE,
  FETCH_BUSINESS_UNITS_ATTEMPT,
  FETCH_BUSINESS_UNITS_SUCCESS,
  FETCH_BUSINESS_UNITS_ERROR,
};

// Notification Subscriptions (per-user, per-business-unit)
export const fetchNotificationSubscriptions = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: FETCH_NOTIFICATION_SUBSCRIPTIONS_ATTEMPT });
      const { data } = await notificationService.fetchSubscriptions();
      dispatch({ type: FETCH_NOTIFICATION_SUBSCRIPTIONS_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: FETCH_NOTIFICATION_SUBSCRIPTIONS_ERROR, payload: err });
    }
  };
};

export const createNotificationSubscription = (subscriptionData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
      const data = await notificationService.createSubscription(subscriptionData);
      dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: err });
    }
  };
};

export const createNotificationSubscriptionsBatch = (subscriptionsData) => {
  return async (dispatch) => {
    dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
    const results = await settleSequentially(subscriptionsData, data => notificationService.createSubscription(data));
    const failures = results.filter(result => result.status === "rejected");
    if (failures.length) {
      const error = new Error(`${failures.length} of ${results.length} subscription operations failed`);
      error.failures = failures.map(result => result.reason);
      dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: error });
      return { success: false, results };
    }
    dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { data: results.map(result => result.value) } });
    return { success: true, results };
  };
};

export const createNotificationSubscriptionResetState = () => {
  return (dispatch) => {
    dispatch({ type: CREATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE });
  };
};

export const deleteNotificationSubscription = (id) => {
  return async (dispatch) => {
    try {
      dispatch({ type: DELETE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
      await notificationService.deleteSubscription(id);
      dispatch({ type: DELETE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { id } });
    } catch (err) {
      dispatch({ type: DELETE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: err });
    }
  };
};

export const deleteNotificationSubscriptionResetState = () => {
  return (dispatch) => {
    dispatch({ type: DELETE_NOTIFICATION_SUBSCRIPTION_RESET_STATE });
  };
};

export const toggleNotificationSubscription = (id) => {
  return async (dispatch) => {
    try {
      dispatch({ type: TOGGLE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
      const data = await notificationService.toggleSubscription(id);
      dispatch({ type: TOGGLE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { data, id } });
    } catch (err) {
      dispatch({ type: TOGGLE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: err });
    }
  };
};

export const updateNotificationSubscription = (id, subscriptionData) => {
  return async (dispatch) => {
    try {
      dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
      const data = await notificationService.updateSubscription(id, subscriptionData);
      dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { data, id } });
    } catch (err) {
      dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: err });
    }
  };
};

export const saveNotificationSubscriptionOperations = (operations) => {
  return async (dispatch) => {
    dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_ATTEMPT });
    const execute = operation => {
      if (operation.action === "create") return notificationService.createSubscription(operation.data);
      if (operation.action === "update") return notificationService.updateSubscription(operation.id, operation.data);
      if (operation.action === "delete") return notificationService.deleteSubscription(operation.id);
      return Promise.reject(new Error(`Unsupported subscription operation: ${operation.action}`));
    };
    const results = await settleSequentially(operations, execute);
    const failures = results.filter(result => result.status === "rejected");
    if (failures.length) {
      const error = new Error(`${failures.length} of ${results.length} subscription operations failed`);
      error.failures = failures.map(result => result.reason);
      dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_ERROR, payload: error });
      return { success: false, results };
    }
    dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_SUCCESS, payload: { data: results.map(result => result.value) } });
    return { success: true, results };
  };
};

export const updateNotificationSubscriptionResetState = () => {
  return (dispatch) => {
    dispatch({ type: UPDATE_NOTIFICATION_SUBSCRIPTION_RESET_STATE });
  };
};

// Business Units for dropdown
export const fetchBusinessUnits = () => {
  return async (dispatch) => {
    try {
      dispatch({ type: FETCH_BUSINESS_UNITS_ATTEMPT });
      const { data } = await notificationService.fetchBusinessUnits();
      dispatch({ type: FETCH_BUSINESS_UNITS_SUCCESS, payload: { data } });
    } catch (err) {
      dispatch({ type: FETCH_BUSINESS_UNITS_ERROR, payload: err });
    }
  };
};
